import { Avatar, Box, Chip, FormHelperText, Link } from "@mui/material";
import { AutogeneratedDraftsContext } from "contexts/AutogenerateDrafts";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import { SavedBrandContext } from "contexts/SavedBrand";
import { SavedBrandCollectionsContext } from "contexts/SavedBrandCollections";
import { forwardRef, useContext, useState } from "react";
import { BentoBrand, LOCATIONS, SearchParams } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import EmailButton from "components/EmailButton";
import { DiscoverSearchChipColor } from "utils/color";
import { getSourceLabel } from "utils/influencer";
import { generateInitials } from "utils/string";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";
import { useCollection } from "utils/useCollection";
import { useDrafts } from "utils/useDrafts";

import DateOrEmail from "./Annotations/DateOrEmail";
import InCollection from "./Annotations/InCollection";
import LeftActionButton from "./Annotations/LeftActionButton";
import styles from "./styles";

interface SelectedBrandProps {
  bentoBrand: BentoBrand;
  collectionId?: number;
  isBulkMode?: boolean;
  fromDeleteDraft?: boolean;
  deleteDraft?: () => void;
  isDefaultCollection?: boolean;
}

const SelectedBrand = forwardRef(
  (
    {
      bentoBrand,
      collectionId = -1,
      isBulkMode,
      fromDeleteDraft = false,
      isDefaultCollection = false,
    }: SelectedBrandProps,
    ref,
  ) => {
    const { removeDraftFromList, removeDraftApiCall } = useDrafts();
    // Contexts
    const {
      selectAll,
      setSelectAll,
      handleRemoveDebounced,
      removeLoading,
      bulkBrands,
      setBulkBrands,
    } = useContext(SavedBrandContext);
    const { handleOpenQuickSendIndividual, quickSendBrandId } = useContext(
      QuickSendDrawerContext,
    );
    const { selectedCollection } = useContext(SavedBrandCollectionsContext);
    const { frozenIds } = useContext(AutogeneratedDraftsContext);
    const { isDraftBrand, isAutogeneratedDraftBrand } = useCollection();

    const isSelected = quickSendBrandId === bentoBrand?.id;
    const isFrozen = frozenIds?.includes(Number(bentoBrand?.id));
    const isChecked = Boolean(
      bulkBrands?.find((x) => Number(x.id) === Number(bentoBrand.id)),
    );
    const [imgError, setImgError] = useState<boolean>(false);

    const handleDelete = async (e: CustomEvent) => {
      e.stopPropagation();

      // When user remove drafts from the Popup Dialog
      if (fromDeleteDraft) {
        removeDraftFromList(bentoBrand);
        setBulkBrands((prev) => {
          const copy = makeDeepCopy(prev);
          const index = prev.findIndex(
            (b) => Number(b.id) === Number(bentoBrand.id),
          );
          if (index > -1) {
            copy[index]["hasOutreachDraft"] = false;
          }
          return copy;
        });
        await removeDraftApiCall(
          e,
          bentoBrand,
          "Write Me Drafts Dialog",
          false,
        );
      } else {
        handleRemoveDebounced(bentoBrand, collectionId);
      }
    };

    const handleSelect = () => {
      if (selectAll) {
        setSelectAll(false);
      }
      setBulkBrands((prev) => {
        const copy = makeDeepCopy(prev);
        const index = prev.findIndex(
          (b) => Number(b.id) === Number(bentoBrand.id),
        );
        if (index > -1) {
          copy.splice(index, 1);
        } else {
          copy.push(bentoBrand);
        }
        return copy;
      });
    };

    const clickOnBrand = () => {
      if (isFrozen) return;
      handleOpenQuickSendIndividual(
        bentoBrand?.id,
        "Favorites List",
        undefined,
        true,
      );
    };

    const goToInstagram = (e: CustomEvent) => {
      e.stopPropagation();
      trackEvent("Instagram Link Viewed", {
        "Instagram Username": bentoBrand.instagramUsername,
      });
      window.open(
        `https://instagram.com/${bentoBrand.instagramUsername}`,
        "_blank",
      );
    };

    const matchedLocation = LOCATIONS?.find(
      (x) => x.key === bentoBrand?.countryCode?.toLowerCase(),
    );
    const location = matchedLocation
      ? matchedLocation?.name
      : bentoBrand?.countryCode;

    const wrapLength = 22;

    return (
      <Box
        ref={ref}
        sx={[styles.brandContainer, isSelected && styles.selected]}
        onClick={clickOnBrand}
        key={`cart-brand-${bentoBrand.id}`}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            width: "auto",
            mr: 0.5,
          }}
        >
          <LeftActionButton
            bentoBrand={bentoBrand}
            isBulkMode={Boolean(isBulkMode)}
            handleDelete={handleDelete}
            handleSelect={handleSelect}
            isChecked={isChecked}
            removeLoading={removeLoading}
            isFrozen={isFrozen}
          />
        </Box>

        <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={styles.brandImage}>
              {!imgError && bentoBrand.logoUrl ? (
                <img
                  src={bentoBrand.logoUrl}
                  alt={`${bentoBrand.normalizedName} logo`}
                  onError={() => setImgError(true)}
                />
              ) : (
                <Avatar sx={styles.avatar}>
                  {generateInitials(bentoBrand.normalizedName)}
                </Avatar>
              )}
            </Box>

            <Box sx={{ flexGrow: 1 }}>
              <Box sx={styles.brandName}>
                {bentoBrand.normalizedName}
                {!isFrozen &&
                  (bentoBrand?.hasOutreachDraft ||
                    isDraftBrand(bentoBrand) ||
                    isAutogeneratedDraftBrand(bentoBrand)) && (
                    <>
                      ,{" "}
                      {isAutogeneratedDraftBrand(bentoBrand) ? (
                        <Box component="span" sx={styles.reco}>
                          Reco{" "}
                          <Box
                            component="i"
                            className="fa-solid fa-wand-magic-sparkles"
                          />
                        </Box>
                      ) : (
                        <Box component="span" sx={styles.draft}>
                          Draft{" "}
                        </Box>
                      )}
                    </>
                  )}
                {isFrozen && <Box sx={styles.frozen}>Generating...</Box>}
              </Box>

              {bentoBrand.instagramUsername && (
                <Link
                  target="_blank"
                  onClick={goToInstagram}
                  sx={{ fontSize: 12 }}
                >
                  @
                  {bentoBrand?.instagramUsername?.length > wrapLength
                    ? `${bentoBrand?.instagramUsername.substring(
                        0,
                        wrapLength,
                      )}...`
                    : bentoBrand.instagramUsername}
                </Link>
              )}
            </Box>
          </Box>

          <Box sx={{ mt: 0.5 }}>
            {selectedCollection?.id === 0 && (
              <InCollection bentoBrand={bentoBrand} />
            )}
            <DateOrEmail bentoBrand={bentoBrand} />

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
              {location && (
                <Chip
                  sx={{
                    backgroundColor:
                      DiscoverSearchChipColor[SearchParams.LOCATION_DISCOVER],
                    fontSize: 8,
                    height: 16,
                  }}
                  size="small"
                  label={location}
                />
              )}
              {[
                ...(bentoBrand?.mainCategoryNames || []),
                ...(bentoBrand?.secondaryCategoryNames || []),
              ]
                ?.slice(0, 2)
                .map((category, index) => (
                  <Chip
                    sx={{
                      backgroundColor:
                        DiscoverSearchChipColor[
                          SearchParams.CATEGORY_ID_DISCOVER
                        ],
                      fontSize: 8,
                      height: 16,
                    }}
                    size="small"
                    label={category}
                    key={index}
                  />
                ))}
            </Box>

            {isDefaultCollection && (
              <FormHelperText
                sx={{
                  backgroundColor: "white",
                  fontSize: 10,
                  color: "secondary.dark",
                  mt: 0.5,
                }}
              >
                {getSourceLabel(bentoBrand)}
              </FormHelperText>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {!fromDeleteDraft && <EmailButton isFrozen={isFrozen} />}
        </Box>
      </Box>
    );
  },
);

export default SelectedBrand;
